import {
  type BodyOf,
  type ErrorEnvelope,
  type FieldError,
  type ParamsOf,
  type QueryOf,
  resolvePath,
  type RouteContract,
  type SuccessOf,
} from "@fleetminder/contracts";

import { loadBrowserEnvironment } from "./runtime-env.js";

export type ApiErrorCategory =
  | "authorization"
  | "conflict"
  | "field"
  | "unexpected";

export type ApiFieldError = Readonly<FieldError>;

export class WebApiError extends Error {
  readonly category: ApiErrorCategory;
  readonly code: string;
  readonly correlationId?: string | undefined;
  readonly fields: readonly ApiFieldError[];
  readonly status: number;

  constructor(input: {
    readonly category: ApiErrorCategory;
    readonly code: string;
    readonly correlationId?: string | undefined;
    readonly fields?: readonly ApiFieldError[] | undefined;
    readonly message: string;
    readonly status: number;
  }) {
    super(input.message);
    this.name = "WebApiError";
    this.category = input.category;
    this.code = input.code;
    this.correlationId = input.correlationId;
    this.fields = input.fields ?? [];
    this.status = input.status;
  }
}

/**
 * What a failure should carry. Read defensively: a proxy or a crash in front
 * of the API can answer with anything, so every field may be missing.
 */
type ApiErrorEnvelope = {
  readonly error?: Partial<ErrorEnvelope["error"]>;
};

export type ApiRequestOptions = Omit<RequestInit, "body" | "headers"> & {
  readonly body?: unknown;
  readonly headers?: HeadersInit;
  readonly organizationId?: string;
};

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
  fetcher: typeof fetch = fetch,
): Promise<T> {
  const env = loadBrowserEnvironment();
  const {
    body,
    headers: requestHeaders,
    organizationId,
    ...requestOptions
  } = options;
  const headers = new Headers(requestHeaders);

  headers.set("accept", "application/json");
  if (body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (organizationId) {
    headers.set("x-fleetminder-organization-id", organizationId);
  }

  const response = await fetcher(
    new URL(`/api/v1${path}`, env.VITE_API_BASE_URL),
    {
      ...requestOptions,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      credentials: "include",
      headers,
    },
  );

  if (!response.ok) {
    throw await toWebApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * What a call through a contract needs besides the contract itself. Each part
 * is required exactly when the contract has it, so a route with path
 * parameters cannot be called without them.
 */
export type CallOptions<Contract extends RouteContract> = Omit<
  ApiRequestOptions,
  "body" | "method"
> &
  (keyof ParamsOf<Contract> extends never
    ? { readonly params?: ParamsOf<Contract> }
    : { readonly params: ParamsOf<Contract> }) &
  (BodyOf<Contract> extends undefined
    ? { readonly body?: undefined }
    : { readonly body: BodyOf<Contract> }) &
  (QueryOf<Contract> extends undefined
    ? { readonly query?: undefined }
    : { readonly query?: QueryOf<Contract> });

/**
 * Calls a route through its contract. The URL is built from the contract's
 * path, and the result is typed by the contract's success response, so a
 * screen neither writes a URL nor declares what comes back: both come from
 * the one definition the API is registered with.
 */
export function callApi<Contract extends RouteContract>(
  contract: Contract,
  ...[options, fetcher]: keyof ParamsOf<Contract> extends never
    ? BodyOf<Contract> extends undefined
      ? [options?: CallOptions<Contract>, fetcher?: typeof fetch]
      : [options: CallOptions<Contract>, fetcher?: typeof fetch]
    : [options: CallOptions<Contract>, fetcher?: typeof fetch]
): Promise<SuccessOf<Contract>> {
  // The options' parts are typed per contract above; here they only need to
  // be taken apart and passed on.
  const { body, params, query, ...requestOptions } = (options ?? {}) as Omit<
    ApiRequestOptions,
    "body" | "method"
  > & {
    readonly body?: unknown;
    readonly params?: unknown;
    readonly query?: unknown;
  };
  // An organization's routes name it in the path, and the API also wants it
  // in the header; the path's value is used unless the caller says otherwise.
  const organizationId =
    requestOptions.organizationId ??
    (params as { readonly organizationId?: string } | undefined)
      ?.organizationId;
  return apiRequest<SuccessOf<Contract>>(
    resolvePath(contract, (params ?? {}) as ParamsOf<Contract>, query as never),
    {
      ...requestOptions,
      ...(body === undefined ? {} : { body }),
      method: contract.method,
      ...(organizationId ? { organizationId } : {}),
    },
    fetcher,
  );
}

/**
 * A React Query key for a call, derived from the contract and its inputs.
 * Organization-scoped routes sit under the organization's key, so
 * invalidating an organization refreshes everything read within it.
 */
export function routeQueryKey<Contract extends RouteContract>(
  contract: Contract,
  params: ParamsOf<Contract>,
  query?: QueryOf<Contract>,
): readonly unknown[] {
  const organizationId = (params as { readonly organizationId?: string })
    .organizationId;
  return [
    ...(organizationId ? queryKeys.organization(organizationId) : []),
    contract.method,
    contract.path,
    params,
    ...(query === undefined ? [] : [query]),
  ];
}

export async function toWebApiError(response: Response): Promise<WebApiError> {
  const body = (await response.json().catch(() => undefined)) as
    | ApiErrorEnvelope
    | undefined;
  const error = body?.error;

  return new WebApiError({
    category: categorizeApiError(response.status, error?.fields),
    code: error?.code ?? "request_failed",
    ...(error?.correlationId ? { correlationId: error.correlationId } : {}),
    ...(error?.fields ? { fields: error.fields } : {}),
    message:
      error?.message ??
      "We couldn't reach FleetMinder. Check your connection and try again.",
    status: response.status,
  });
}

export function categorizeApiError(
  status: number,
  fields?: readonly ApiFieldError[],
): ApiErrorCategory {
  if (fields && fields.length > 0) return "field";
  if (status === 401 || status === 403) return "authorization";
  if (status === 409) return "conflict";
  return "unexpected";
}

export function getFieldError(
  error: unknown,
  path: string,
): string | undefined {
  if (!(error instanceof WebApiError)) return undefined;
  return error.fields.find((field) => field.path === path)?.message;
}

/**
 * What a form says above its fields when a save fails: nothing when every
 * problem is marked on a field the form shows, and otherwise the problems it
 * cannot place, so a refused save never just stops. `shownPaths` are the
 * fields the form can mark right now. `fallback` covers a dropped connection
 * or a server failure, where the server's words are no help.
 */
export function formErrorMessage(
  error: unknown,
  shownPaths: readonly string[],
  fallback: string,
): string | undefined {
  if (!error) return undefined;
  if (!(error instanceof WebApiError)) return fallback;
  if (error.fields.length === 0)
    return error.status >= 500 ? fallback : error.message;
  const unplaced = error.fields.filter(
    (field) => !shownPaths.includes(field.path),
  );
  if (unplaced.length === 0) return undefined;
  return [...new Set(unplaced.map((field) => field.message))].join(" ");
}

export const queryKeys = {
  organization: (organizationId: string) =>
    ["organization", organizationId] as const,
  organizationMembers: (organizationId: string) =>
    [...queryKeys.organization(organizationId), "members"] as const,
  organizationInvitations: (organizationId: string) =>
    [...queryKeys.organization(organizationId), "invitations"] as const,
  organizationDrivers: (organizationId: string) =>
    [...queryKeys.organization(organizationId), "drivers"] as const,
  organizationVehicles: (
    organizationId: string,
    filters: { readonly search?: string; readonly status?: string } = {},
  ) =>
    [
      ...queryKeys.organization(organizationId),
      "vehicles",
      { search: filters.search ?? "", status: filters.status ?? "" },
    ] as const,
  vehicle: (organizationId: string, vehicleId: string) =>
    [...queryKeys.organization(organizationId), "vehicle", vehicleId] as const,
};
